<template>
  <el-container>
    <main class="page__container">
      <div class="container">
        <div class="cameras-list__title">
          <h2 class="page__title">Список камер</h2>
          <el-button :type="showDeletedCameras ? 'info' : 'primary'" @click="handleShowDeletedCameras">
            <template v-if="!showDeletedCameras">Показать удаленные</template>
            <template v-else>Скрыть удаленные</template>
          </el-button>
        </div>
        <search
          :query.sync="query"
          placeholder="Несколько значений через пробел, фразу — в кавычках"
          @clear="clearSearch"
          @search="search"
        />
        <cameras-table
          ref="camerasTable"
          :query.sync="query"
          :status-filter="statusFilter"
          :cameras="items"
          :cameras-list-loading="camerasListLoading"
          :show-deleted-cameras="showDeletedCameras"
          @update:status-filter="setStatusFilter"
          @search="search"
          @sort="sort"
          @selection-change="handleSelectionChange"
        />
        <el-pagination
          hide-on-single-page
          class="list__pagination"
          layout="prev, pager, next"
          :page-size="count"
          :total="total"
          :current-page="page"
          @current-change="changePage"
        />
      </div>
    </main>
    <el-footer v-if="showManageFooter" class="page__active_bar">
      <div class="page__active_button--left">
        <el-dropdown trigger="click" @command="(action) => action()">
          <el-button :loading="camerasListLoading">
            {{ count }} <i class="el-icon-arrow-down el-icon--right" />
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item v-for="(item, i) in countsList" :key="i" :disabled="camerasListLoading" :command="() => handleCountChange(item)">
              {{ item }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <span class="label"> Всего результатов: {{ total }}</span>
      </div>
      <el-button
        v-if="$can('create', 'cameras')"
        class="page__active_button"
        icon="el-icon-plus"
        type="primary"
        @click="$router.push({ name: 'cameras:create' })"
      >
        Добавить
      </el-button>
      <el-dropdown
        v-if="selected.length > 0"
        class="page__active_button"
        trigger="click"
        @command="(action) => action()"
      >
        <el-badge :value="selected.length" :max="99" class="item">
          <el-button :loading="loading" type="primary">
            Редактировать камеры <i class="el-icon-arrow-down el-icon--right" />
          </el-button>
        </el-badge>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item :disabled="loading" v-for="(item, i) in dropdownItems" :key="i" :command="item.action">
            {{ item.title }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </el-footer>
    <component
      :visible.sync="showDialog"
      :cameras="selected"
      :is="activeDialog"
      @closed="activeDialog = ''"
      @updated="onUpdated"
    />
  </el-container>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from "vuex";
import Search from "@/components/Search";
import list from "@/core/mixins/list";
import CamerasTable from "@/components/Cameras/components/CamerasTable";
import CamerasTariffDialog from "@/components/Cameras/components/dialogs/CamerasTariffDialog";
import CamerasDvrServerDialog from "@/components/Cameras/components/dialogs/CamerasDvrServerDialog";
import CamerasOwnerDialog from "@/components/Cameras/components/dialogs/CamerasOwnerDialog";
import { checkPermission } from "@/core/helpers/permissions";
import { ApiService } from "@/api";

export default {
  name: "CamerasList",
  components: {
    CamerasOwnerDialog,
    CamerasDvrServerDialog,
    CamerasTariffDialog,
    CamerasTable,
    search: Search,
  },
  mixins: [list],
  data() {
    const dropdownItems = [
      { title: "Владелец", action: () => this.setActiveDialog("CamerasOwnerDialog"), permission: "cameras:update" },
      {
        title: "Тариф",
        action: () => this.setActiveDialog("CamerasTariffDialog"),
        permission: "cameras_archive_depth:update",
      },
      {
        title: "Медиа-сервер",
        action: () => this.setActiveDialog("CamerasDvrServerDialog"),
        permission: "cameras_dvr_server:update",
      },
      { title: "Активировать", action: () => this.onUpdateStateCameras(true), permission: "cameras_state:update" },
      { title: "Деактивировать", action: () => this.onUpdateStateCameras(false), permission: "cameras_state:update" },
      { title: "Перезагрузить", action: () => this.restartCameras(), permission: "cameras_state:update" },
      { title: "Удалить", action: () => this.onDeleteCameras(), permission: "cameras:delete" },
    ].filter(({ permission }) => checkPermission(permission));
    const countsList = [ 10, 20, 50, 100 ];

    return {
      loading: false,
      query: "",
      selected: [],
      showDialog: false,
      activeDialog: "",
      showDeletedCameras: false,
      dropdownItems,
      countsList,
    };
  },
  computed: {
    ...mapState("cameras", {
      items: (state) => state.cameras.data,
      total: (state) => state.cameras.total,
      count: (state) => state.cameras.count,
      sortColumnName: (state) => state.cameras.sort_column_name,
      sortDirection: (state) => state.cameras.sort_direction,
      camerasListLoading: (state) => state.camerasListLoading,
    }),
    ...mapGetters("cameras", ["getQuery", "getStatusFilter"]),
    statusFilter() {
      return this.getStatusFilter;
    },
    showManageFooter() {
      return this.$can("create", "cameras") || this.$can("update", "cameras");
    },
  },
  created() {
    this.setList({
      data: [],
      total: 0,
      count: 50,
    });
  },
  methods: {
    ...mapActions("cameras", {
      getList: "GET_CAMERAS",
    }),
    ...mapMutations("cameras", {
      setQuery: "SET_QUERY",
      setStatusFilter: "SET_STATUS_FILTER",
      setSorting: "SET_SORTING",
      setList: "SET_LIST_CAMERAS",
    }),
    onUpdateStateCameras(state) {
      this.$confirm(
        `Вы действительно хотите ${state ? "<b>активировать</b>" : "<b>деактивировать</b>"} выбранные камеры`,
        state ? "Активация камер" : "Деактивация камер",
        {
          type: "warning",
          confirmButtonClass: state ? "el-button--success" : "el-button--danger",
          confirmButtonText: state ? "Активировать" : "Деактивировать",
          dangerouslyUseHTMLString: true,
        }
      ).then(() => {
        this.loading = true;
        ApiService.cameras
          .updateCamerasState(state, this.selected)
          .then(() => {
            this.$message.success(`Камеры успешно ${state ? "активированы" : "деактивированы"}`);
            this.onUpdated();
          })
          .catch((e) => {
            this.$message.error(
              e.response.data.error || `Произошла ошибка при ${state ? "активации" : "деактивации"} камер`
            );
          })
          .finally(() => (this.loading = false));
      });
    },
    restartCameras() {
      this.$confirm(
        "Вы действительно хотите перезагрузить выбранные камеры",
        "Перезагрузка камер",
        {
          type: "warning",
          confirmButtonClass: "el-button--danger",
          confirmButtonText: "Перезагрузить",
          dangerouslyUseHTMLString: true,
        }
      ).then(() => {
        this.loading = true;
        ApiService.cameras
          .updateCamerasState(false, this.selected)
          .then(() => setTimeout(() => {
            ApiService.cameras.updateCamerasState(true, this.selected)
              .then(() => {
                this.$message.success("Камеры успешно перезагружены");
                this.onUpdated();
              })
              .catch((e) => {
                this.$message.error(
                  e.response.data.error || "Произошла ошибка при включении камер"
                );
              })
              .finally(() => (this.loading = false));
          }, 1000))
          .catch((e) => {
            this.$message.error(e.response.data.error || "Произошла ошибка при выключении камер");
            this.loading = false;
          });
      });
    },
    onDeleteCameras() {
      const selectedCameras = this.items.filter((camera) => this.selected.includes(camera.id_camera));
      const lvsCameras = selectedCameras.filter((camera) => camera.is_lvs === 1);
      const lvbCameras = selectedCameras.filter((camera) => camera.is_lvb === 1);

      if (lvsCameras.length > 0 || lvbCameras.length > 0) {
        const lvsNames = lvsCameras.map((camera) => camera.short_name).join(", ");
        const lvbNames = lvbCameras.map((camera) => camera.short_name).join(", ");
        const warnings = [
          lvsCameras.length && `Нельзя удалить камеры, привязанные к LinkVideo.Server: ${lvsNames}`,
          lvbCameras.length && `Нельзя удалить камеры, привязанные к LinkVideo.Bridge: ${lvbNames}`,
        ].filter(Boolean);
        this.$alert(
          warnings.join("; "),
          "Удаление камер",
          {
            confirmButtonText: "Закрыть",
            type: "warning",
          }
        ).catch(() => undefined);
        return;
      }

      this.$confirm(
        `Вы действительно хотите удалить выбранные камеры?`,
        "Удаление камер",
        {
          type: "warning",
          confirmButtonClass: "el-button--danger",
          confirmButtonText: "Удалить",
          dangerouslyUseHTMLString: true
        }
      ).then(() => {
        const selectedCount = this.selected.length;
        this.loading = true;
        ApiService.cameras
          .deleteCameras(this.selected)
          .then((payload) => {
            const messageType = payload.failed > 0 ? "warning" : "success";
            this.$message[messageType](`Удалено ${payload.success} из ${selectedCount}`);
            this.onUpdated();
          })
          .catch((e) => {
            this.$message.error(e.response?.data?.error || "Произошла ошибка при удалении камер");
          })
          .finally(() => (this.loading = false));
      });
    },
    setActiveDialog(dialog) {
      this.activeDialog = dialog;
      this.showDialog = true;
    },
    onUpdated(keepDialogOpen = false) {
      const selected = [...this.selected];
      this.loadData().then(() => {
        if (!keepDialogOpen) {
          this.showDialog = false;
          this.activeDialog = "";
        }
        this.$refs.camerasTable.toggleCamerasSelection(selected);
      });
    },
    handleSelectionChange(selections) {
      this.selected = selections.map((camera) => camera.id_camera);
    },
    handleCountChange(count) {
      this.setList({
        data: [],
        total: this.total,
        count,
      });
      this.loadData()
    },
    clearSearch() {
      this.$refs.camerasTable.clearStatusFilter();
    },
    handleShowDeletedCameras() {
      this.showDeletedCameras = !this.showDeletedCameras;
      const searchTriggered = this.$refs.camerasTable.updateDeletedMode(this.showDeletedCameras);
      if (!searchTriggered) {
        this.loadData();
      }
    },
  },
};
</script>

<style lang="postcss">
.list,
.list__pagination {
  margin: 20px 0;
}
.list__pagination {
  text-align: center;
}
.el-icon-error {
  color: #fa5555;
}
.el-icon-success {
  color: #67c23a;
}
.el-icon-warning {
  color: #409eff;
}
.el-table th > .cell {
  word-break: break-word;
}
.link {
  line-height: 1;
  cursor: pointer;
  outline: 0;
  margin: 0;
  transition: 0.1s;
  color: #409eff;
}
.page__active_button--left > .label {
  font-size: 14px;
  color: #606266;
}
.cameras-list__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
